import {
  useSEO,
  generateOrganizationSchema,
  generateWebPageSchema,
} from "../../utils/seo";

export default function QuemSomos() {
  useSEO({
    title: "Quem somos - Moveideias | Transformação digital em logística",
    description:
      "Conheça a Moveideias: empresa de transformação digital com propósito de gerar transparência e valor através da informação no processo logístico desde 2014.",
    keywords:
      "quem somos, moveideias, história, missão, visão, valores, transformação digital, logística",
    canonical: "/quem-somos",
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateOrganizationSchema(),
        generateWebPageSchema({
          name: "Quem Somos - Moveideias",
          description:
            "Conheça a história, missão, visão e valores da Moveideias.",
          url: "/quem-somos",
        }),
      ],
    },
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(34, 26, 50, 0.8), rgba(34, 26, 50, 0.8)), url('/images/backgrounds/quem-somos-hero.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Quem somos
          </h1>
          <p className="text-xl md:text-2xl text-[#e7eaf6] max-w-4xl mx-auto leading-relaxed">
            Transformamos informação em transparência, eficiência e valor para
            revolucionar a logística do seu negócio.
          </p>
        </div>
      </section>

      {/* Sobre a Moveideias */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
                A MOVEIDEIAS
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                A MOVEIDEIAS é uma empresa de transformação digital com o
                propósito de gerar transparência e valor por meio da informação
                no processo logístico das organizações.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Desde 2014, combinamos experiência em tecnologia, logística e
                gestão de processos de negócios para ajudar empresas a aprimorar
                a gestão, rastreamento e visibilidade de suas operações,
                tornando-as mais integradas e eficientes.
              </p>
            </div>
            <div
              className="h-96 bg-cover bg-center rounded-xl"
              style={{
                backgroundImage: `url('/images/about/moveideias-company.png')`,
              }}
            />
          </div>
        </div>
      </section>

      {/* Propósito, Missão e Visão */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.95), rgba(231, 234, 246, 0.95)), url('/images/backgrounds/mission-vision.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Nosso DNA
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#f28c1a] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-focus-2-line text-2xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#221a32] mb-4">
                Propósito
              </h3>
              <p className="text-gray-600 leading-relaxed">
                "Gerar transparência e valor por meio da informação nos
                processos logísticos."
              </p>
            </div>

            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-compass-line text-2xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#221a32] mb-4">Missão</h3>
              <p className="text-gray-600 leading-relaxed">
                "Ajudar as empresas a aprimorar a gestão, o rastreamento e a
                visibilidade de seus processos logísticos."
              </p>
            </div>

            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#4bb5ab] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-eye-line text-2xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#221a32] mb-4">Visão</h3>
              <p className="text-gray-600 leading-relaxed">
                "Simplificar, com tecnologia, o gerenciamento e a visibilidade
                dos processos logísticos nas empresas."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Nossos Valores */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Nossos valores
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Os princípios que guiam nossa forma de trabalhar e nos relacionar
              com clientes e parceiros.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-[#e7eaf6] p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-[#f28c1a] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-trophy-line text-xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3">
                Resultados
              </h3>
              <p className="text-gray-600">Nossa razão de existência</p>
            </div>

            <div className="bg-[#e7eaf6] p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-[#5a6ab0] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3">Pessoas</h3>
              <p className="text-gray-600">Nosso verdadeiro ativo</p>
            </div>

            <div className="bg-[#e7eaf6] p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-[#4bb5ab] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-hand-heart-line text-xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3">
                Respeito
              </h3>
              <p className="text-gray-600">Nossa força na equipe</p>
            </div>

            <div className="bg-[#e7eaf6] p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-[#f28c1a] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3">
                Confiança
              </h3>
              <p className="text-gray-600">Fideliza os relacionamentos</p>
            </div>

            <div className="bg-[#e7eaf6] p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-[#5a6ab0] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-links-line text-xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3">
                Integração
              </h3>
              <p className="text-gray-600">Gera novos insights</p>
            </div>

            <div className="bg-[#e7eaf6] p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-[#4bb5ab] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-scales-line text-xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#221a32] mb-3">Ética</h3>
              <p className="text-gray-600">É inegociável</p>
            </div>
          </div>
        </div>
      </section>

      {/* Nossa História */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)), url('/images/backgrounds/nossa-historia.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div
              className="h-96 bg-cover bg-center rounded-xl"
              style={{
                backgroundImage: `url('/images/backgrounds/historia-image.jpg')`,
              }}
            />
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
                Nossa história
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Nascemos com uma mentalidade empreendedora e inovadora,
                determinada a transformar os desafios logísticos em
                oportunidades de crescimento.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Nossa trajetória é marcada pela criação de soluções tecnológicas
                que revolucionam a forma como as empresas gerenciam suas
                operações e tomam decisões baseadas em dados.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Nossas Soluções */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Nossas soluções
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Plataformas que refletem nosso compromisso em resolver desafios
              críticos de gestão, integração e eficiência operacional na
              logística.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#e7eaf6] p-8 rounded-xl">
              <div className="w-16 h-16 bg-[#ea5836] rounded-full flex items-center justify-center mb-6">
                <i className="ri-shopping-cart-line text-2xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#221a32] mb-4">
                TeuPedido
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Gestão e visibilidade completa da logística das entregas e
                fortalecimento do relacionamento entre empresas e clientes.
              </p>
            </div>

            <div className="bg-[#e7eaf6] p-8 rounded-xl">
              <div className="w-16 h-16 bg-[#4bb5ab] rounded-full flex items-center justify-center mb-6">
                <i className="ri-truck-line text-2xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#221a32] mb-4">
                MoveCourier
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Rastreabilidade e evidências digitais em tempo real, elevando o
                controle e a confiabilidade das operações de transporte.
              </p>
            </div>

            <div className="bg-[#e7eaf6] p-8 rounded-xl">
              <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mb-6">
                <i className="ri-flow-chart text-2xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#221a32] mb-4">
                MovePipe
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Lançado em 2024, traz automação inteligente e flexível por meio
                de tecnologia no-code, permitindo que gestores construam fluxos
                de trabalho digitais de forma rápida e personalizada.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Nosso jeito de fazer */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(231, 234, 246, 0.95), rgba(231, 234, 246, 0.95)), url('/images/backgrounds/jeito-fazer.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Nosso jeito de fazer
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Mais do que desenvolver sistemas, atuamos como parceiros
                estratégicos de nossos clientes. Cada projeto é uma oportunidade
                de inovar, aprender e gerar valor real.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Acreditamos que a tecnologia deve simplificar, não complicar — e
                que a informação deve ser um agente de transformação.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Nossa cultura é movida por curiosidade, colaboração e propósito.
                Valorizamos talentos que pensam diferente, questionam padrões e
                buscam soluções que façam a diferença no dia a dia das
                operações.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl text-center">
                <div className="w-12 h-12 bg-[#f28c1a] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-lightbulb-line text-xl text-white" />
                </div>
                <h4 className="font-bold text-[#221a32] mb-2">Inovação</h4>
                <p className="text-sm text-gray-600">Soluções criativas</p>
              </div>
              <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl text-center">
                <div className="w-12 h-12 bg-[#5a6ab0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shake-hands-line text-xl text-white" />
                </div>
                <h4 className="font-bold text-[#221a32] mb-2">Parceria</h4>
                <p className="text-sm text-gray-600">
                  Relacionamento estratégico
                </p>
              </div>
              <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl text-center">
                <div className="w-12 h-12 bg-[#4bb5ab] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-xl text-white" />
                </div>
                <h4 className="font-bold text-[#221a32] mb-2">Curiosidade</h4>
                <p className="text-sm text-gray-600">Sempre aprendendo</p>
              </div>
              <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl text-center">
                <div className="w-12 h-12 bg-[#f28c1a] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-heart-line text-xl text-white" />
                </div>
                <h4 className="font-bold text-[#221a32] mb-2">Propósito</h4>
                <p className="text-sm text-gray-600">Fazer a diferença</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nossa Essência */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(34, 26, 50, 0.9), rgba(34, 26, 50, 0.9)), url('/images/backgrounds/nossa-essencia.jpg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Nossa essência
          </h2>
          <p className="text-xl md:text-2xl text-[#e7eaf6] mb-8 max-w-4xl mx-auto leading-relaxed">
            Na Moveideias, inovação é movimento.
          </p>
          <p className="text-xl text-[#e7eaf6] mb-8 max-w-3xl mx-auto leading-relaxed">
            É o que nos motiva a transformar informação em clareza, tecnologia
            em resultados e desafios em conquistas diárias.
          </p>
        </div>
      </section>
    </div>
  );
}
